import React, { useState, useEffect } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import { useAuth } from './AuthContext'
import { useIsMobile } from './useIsMobile'
import { detectTracking } from './tracking'
import { CopyButton } from './CopyButton'
import { ShoppingCart, Truck, Package, CheckCircle, Clock, AlertCircle, RefreshCw, X, Eye, ExternalLink, Repeat, Ban } from 'lucide-react'

const IVA = 0.23

import { C } from './theme'
import { guardarCarrinho } from './carrinhoStorage'

const fmt = (n) => Number(n || 0).toFixed(2)

// Regra única da 1.ª prestação — a mesma do Financeiro, dos emails e da BD:
//   o que entrou de facto > o sinal que o cliente escolheu > metade.
// A 2.ª é SEMPRE `total − 1.ª`. Se a view não trouxer estes campos, cai em
// total/2 — o comportamento de sempre, sem partir nada.
// Encomendas de fornecedor com pagamento total: paga-se tudo na compra e nao
// ha 2.a prestacao. Vem de encomendas.tipo_pagamento, exposto na view do
// cliente desde 14/08/2026 — antes disso este ecra mostrava ao cliente metade
// por pagar numa encomenda que ele ja tinha liquidado por inteiro.
const pagaTudoDeUmaVez = (enc) => String(enc?.tipo_pagamento) === '100'

const primeiraPrestacao = (enc, total) =>
  enc?.pago_1a_valor != null     ? Number(enc.pago_1a_valor)
  : pagaTudoDeUmaVez(enc)        ? total
  : enc?.sinal_escolhido != null ? Number(enc.sinal_escolhido)
  : total / 2
const pctDoTotal = (valor, total) => total > 0 ? Math.round((valor / total) * 1000) / 10 : 50
const fmtPct = (n) => String(Math.round(Number(n) * 10) / 10).replace('.', ',')
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—'

function TrackingLink({ transportadora, codigo, prefix, size = 'sm', onClick }) {
  if (!codigo) return null
  const det = detectTracking(transportadora, codigo)
  if (!det) return <span style={{ color: C.primary, fontWeight: '600' }}>{codigo}</span>
  const fontSize = size === 'lg' ? '13px' : '11px'
  return (
    <a
      href={det.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => { e.stopPropagation(); onClick?.(e) }}
      style={{ color: C.primary, fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize }}
      title={`Abrir tracking ${det.nome}${det.fallback ? ' (pesquisa)' : ''}`}
    >
      {prefix && <span style={{ color: C.textMuted, fontWeight: '500' }}>{prefix}</span>}
      {/* Há transportadoras que dão um endereço por envio em vez de um código
          (a ONTIME é uma). Mostrar o endereço inteiro como texto do link enche
          a linha de caracteres que não dizem nada — escreve-se o nome delas. */}
      <span style={{ borderBottom: `1px dashed ${C.primary}`, paddingBottom: '1px' }}>
        {/^https?:\/\//i.test(String(codigo)) ? `Seguir envio · ${det.nome}` : codigo}
      </span>
      <ExternalLink size={size === 'lg' ? 12 : 10} />
    </a>
  )
}

export default function MinhasEncomendas({ viewAsEmail, setActivePage }) {
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [repetindo, setRepetindo] = useState(false)
  const [encomendas, setEncomendas] = useState([])
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState({ tipo: '', texto: '' })

  // Modal de detalhe
  const [modalEnc, setModalEnc] = useState(null)
  const [modalItens, setModalItens] = useState([])
  const [modalLoading, setModalLoading] = useState(false)
  const [uploadingComp, setUploadingComp] = useState(null) // '1a' | '2a' | null
  const [historicoPrecos, setHistoricoPrecos] = useState({}) // produto_nome → preço da compra anterior
  const [pagamentosExpand, setPagamentosExpand] = useState(false)
  const [cancelando, setCancelando] = useState(false)
  // Dados bancarios para o cliente fazer transferencia
  const [iban, setIban] = useState('')
  const [nib, setNib] = useState('')
  const [bancoNome, setBancoNome] = useState('')
  const [bancoTitular, setBancoTitular] = useState('')
  useEffect(() => {
    let cancelado = false
    ;(async () => {
      const { data } = await supabase.from('config_sistema').select('chave, valor').in('chave', ['iban','nib','banco_nome','banco_titular'])
      if (cancelado || !data) return
      for (const r of data) {
        if (r.chave === 'iban') setIban(r.valor || '')
        else if (r.chave === 'nib') setNib(r.valor || '')
        else if (r.chave === 'banco_nome') setBancoNome(r.valor || '')
        else if (r.chave === 'banco_titular') setBancoTitular(r.valor || '')
      }
    })()
    return () => { cancelado = true }
  }, [])

  const cancelarEncomenda = async () => {
    if (!modalEnc) return
    if (modalEnc.estado !== 'pendente') { setMsg({ tipo: 'erro', texto: 'Só encomendas pendentes podem ser canceladas.' }); return }
    const motivo = window.prompt('Por que motivo quer cancelar? (opcional, será partilhado com o admin)')
    if (motivo === null) return
    if (!window.confirm('Confirmar cancelamento da encomenda? Esta ação não pode ser revertida.')) return
    setCancelando(true)
    setMsg({ tipo: '', texto: '' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/mudar-estado-encomenda`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ encomenda_id: modalEnc.id, novo_estado: 'cancelado', motivo: motivo.trim() || null }),
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.erro || 'erro desconhecido')
      const atualizado = { ...modalEnc, estado: 'cancelado' }
      setModalEnc(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalEnc.id ? atualizado : e))
      setMsg({ tipo: 'ok', texto: 'Encomenda cancelada. Recebeu uma confirmação por email.' })
    } catch (err) {
      setMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setCancelando(false)
    }
  }

  const uploadComprovativo = async (file, prestacao) => {
    if (!modalEnc || !file) return
    const isPdf = file.type === 'application/pdf'
    const isImg = file.type?.startsWith('image/')
    if (!isPdf && !isImg) { setMsg({ tipo: 'erro', texto: 'Apenas PDF ou imagem.' }); return }
    if (file.size > 10 * 1024 * 1024) { setMsg({ tipo: 'erro', texto: 'Ficheiro demasiado grande (max 10 MB).' }); return }
    // Bloqueio se o admin ja aprovou — defesa em profundidade (backend tambem bloqueia)
    const jaAprovado = prestacao === '1a' ? modalEnc.pago_1a_prestacao
      : prestacao === '2a' ? modalEnc.pago_2a_prestacao
      : (modalEnc.pago_1a_prestacao || modalEnc.pago_2a_prestacao)
    if (jaAprovado) {
      setMsg({ tipo: 'erro', texto: 'Este pagamento ja foi aprovado pelo administrador e nao pode ser alterado.' }); return
    }
    // Confirmar substituicao se ja existe comprovativo
    const urlExistente = prestacao === '1a' ? modalEnc.comprovativo_1a_url
      : prestacao === '2a' ? modalEnc.comprovativo_2a_url
      : (modalEnc.comprovativo_1a_url || modalEnc.comprovativo_2a_url)
    if (urlExistente) {
      const ok = window.confirm('Ja tens um comprovativo submetido para esta prestacao. Queres substitui-lo pelo novo ficheiro?')
      if (!ok) return
    }
    setUploadingComp(prestacao)
    setMsg({ tipo: '', texto: '' })
    try {
      const ext = file.name.split('.').pop().toLowerCase()
      const path = `${user.id}/${modalEnc.id}-${prestacao}.${ext}`
      const { error: upErr } = await supabase.storage.from('comprovativos').upload(path, file, { upsert: true, contentType: file.type })
      if (upErr) throw new Error('Upload: ' + upErr.message)
      const agora = new Date().toISOString()
      // prestacao 'total' = pagamento 100% upfront → grava em ambas as prestações
      // Submeter um novo comprovativo limpa a marca de recusa — senao a encomenda
      // ficava assinalada como recusada para sempre, mesmo depois de corrigida.
      const limpar1a = { comprovativo_1a_rejeitado_em: null, comprovativo_1a_rejeitado_motivo: null }
      const limpar2a = { comprovativo_2a_rejeitado_em: null, comprovativo_2a_rejeitado_motivo: null }
      const update = prestacao === 'total'
        ? { comprovativo_1a_url: path, comprovativo_1a_em: agora, comprovativo_2a_url: path, comprovativo_2a_em: agora, ...limpar1a, ...limpar2a }
        : prestacao === '1a'
          ? { comprovativo_1a_url: path, comprovativo_1a_em: agora, ...limpar1a }
          : { comprovativo_2a_url: path, comprovativo_2a_em: agora, ...limpar2a }
      // .select('id') força retorno das rows afectadas. Se 0 (sessão expirada
      // ou RLS filtrou), o supabase-js não dá erro — temos de detectar nós.
      const { data: rows, error: dbErr } = await supabase.from('encomendas')
        .update(update).eq('id', modalEnc.id).select('id')
      if (dbErr) throw new Error('BD: ' + dbErr.message)
      if (!rows || rows.length === 0) {
        throw new Error('Sessão expirada — recarrega a página e faz login novamente.')
      }
      const atualizado = { ...modalEnc, ...update }
      setModalEnc(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalEnc.id ? atualizado : e))
      // Notificar admin por email (não bloqueia UX se falhar)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        await fetch(`${SUPABASE_URL}/functions/v1/notificar-comprovativo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ encomenda_id: modalEnc.id, prestacao }),
        })
      } catch (_) { /* silent */ }
      setMsg({ tipo: 'ok', texto: 'Comprovativo enviado. O administrador foi notificado.' })
      setTimeout(() => setMsg({ tipo: '', texto: '' }), 5000)
    } catch (err) {
      setMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setUploadingComp(null)
    }
  }

  const apagarComprovativo = async (prestacao) => {
    if (!modalEnc) return
    // Bloqueio se admin ja aprovou
    const jaAprovado = prestacao === '1a' ? modalEnc.pago_1a_prestacao
      : prestacao === '2a' ? modalEnc.pago_2a_prestacao
      : (modalEnc.pago_1a_prestacao || modalEnc.pago_2a_prestacao)
    if (jaAprovado) {
      setMsg({ tipo: 'erro', texto: 'Este pagamento ja foi aprovado pelo administrador e nao pode ser removido.' }); return
    }
    const ok = window.confirm('Apagar o comprovativo? Terás de anexar novamente. Esta acção não pode ser desfeita.')
    if (!ok) return
    setUploadingComp(prestacao)
    setMsg({ tipo: '', texto: '' })
    try {
      // Detectar se estava em modo total (mesmo path em 1a e 2a)
      const modoTotal = modalEnc.comprovativo_1a_url
        && modalEnc.comprovativo_2a_url
        && modalEnc.comprovativo_1a_url === modalEnc.comprovativo_2a_url
      // Path a remover do storage
      const paths = new Set()
      if (prestacao === '1a' || modoTotal) { if (modalEnc.comprovativo_1a_url) paths.add(modalEnc.comprovativo_1a_url) }
      if (prestacao === '2a' || modoTotal) { if (modalEnc.comprovativo_2a_url) paths.add(modalEnc.comprovativo_2a_url) }
      if (paths.size > 0) {
        const { error: rmErr } = await supabase.storage.from('comprovativos').remove(Array.from(paths))
        if (rmErr) console.warn('Storage remove erro:', rmErr.message)
      }
      // Limpar refs na BD
      const update = (prestacao === 'total' || modoTotal)
        ? { comprovativo_1a_url: null, comprovativo_1a_em: null, comprovativo_2a_url: null, comprovativo_2a_em: null }
        : prestacao === '1a'
          ? { comprovativo_1a_url: null, comprovativo_1a_em: null }
          : { comprovativo_2a_url: null, comprovativo_2a_em: null }
      const { data: rows, error: dbErr } = await supabase.from('encomendas').update(update).eq('id', modalEnc.id).select('id')
      if (dbErr) throw new Error('BD: ' + dbErr.message)
      if (!rows || rows.length === 0) throw new Error('Sessao expirada — recarrega a pagina.')
      const atualizado = { ...modalEnc, ...update }
      setModalEnc(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalEnc.id ? atualizado : e))
      setMsg({ tipo: 'ok', texto: 'Comprovativo removido. Podes anexar um novo.' })
      setTimeout(() => setMsg({ tipo: '', texto: '' }), 5000)
    } catch (err) {
      setMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setUploadingComp(null)
    }
  }

  const verComprovativo = async (path) => {
    if (!path) return
    try {
      const { data, error } = await supabase.storage.from('comprovativos').createSignedUrl(path, 3600)
      if (error || !data?.signedUrl) throw new Error(error?.message || 'erro')
      window.open(data.signedUrl, '_blank')
    } catch (e) {
      setMsg({ tipo: 'erro', texto: 'Erro a abrir: ' + (e?.message || String(e)) })
    }
  }

  const loadEncomendas = async () => {
    setLoading(true)
    // SEGURANÇA: ler da view (exclui campos admin-internos como
    // fornecedor_fatura_numero, pago_*_banco, fatura_notas, lembrete_2a_em).
    // RLS herda da tabela base (cliente_encomendas_select).
    let query = supabase.from('encomendas_cliente_view').select('*').order('created_at', { ascending: false })
    // Modo "vendo como" filtra por email; senão pelo id do utilizador autenticado
    if (viewAsEmail) {
      query = query.eq('cliente_email', viewAsEmail)
    } else {
      query = query.eq('cliente_id', user.id)
    }
    const { data, error } = await query
    if (error) {
      setMsg({ tipo: 'erro', texto: 'Erro ao carregar encomendas: ' + error.message })
    } else {
      setEncomendas(data || [])
    }
    setLoading(false)
  }

  useEffect(() => { loadEncomendas() }, [user, viewAsEmail])

  // Auto-abrir detalhe da encomenda quando se chega vindo do clique numa notificacao
  // (estado_encomenda, fatura, lembrete_1a, pagamento, etc.)
  useEffect(() => {
    if (!encomendas || encomendas.length === 0) return
    try {
      const raw = sessionStorage.getItem('notif_open')
      if (!raw) return
      const data = JSON.parse(raw)
      if (data?.table !== 'encomendas' || !data?.id) return
      sessionStorage.removeItem('notif_open')
      const enc = encomendas.find(e => e.id === data.id)
      if (enc) setTimeout(() => abrirDetalhe(enc), 80)
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encomendas])

  const abrirDetalhe = async (enc) => {
    setModalEnc(enc)
    setModalItens([])
    setHistoricoPrecos({})
    setModalLoading(true)
    const { data } = await supabase
      .from('encomenda_itens')
      .select('*')
      .eq('encomenda_id', enc.id)
      .order('created_at')
    const itens = data || []
    setModalItens(itens)
    setModalLoading(false)

    // Histórico de preços: última compra de cada produto deste cliente em encomendas anteriores
    const targetEmail = viewAsEmail || enc.cliente_email
    if (itens.length > 0 && targetEmail) {
      const nomes = [...new Set(itens.map(i => i.produto_nome).filter(Boolean))]
      if (nomes.length > 0) {
        const { data: hist } = await supabase
          .from('encomenda_itens')
          .select('produto_nome, preco_iva, encomendas!inner(id, cliente_email, created_at, estado)')
          .in('produto_nome', nomes)
          .eq('encomendas.cliente_email', targetEmail)
          .neq('encomendas.estado', 'cancelado')
          .neq('encomendas.id', enc.id)
          .order('created_at', { ascending: false, foreignTable: 'encomendas' })
        const map = {}
        ;(hist || []).forEach(h => {
          // Guarda só o mais recente por produto
          if (!map[h.produto_nome]) map[h.produto_nome] = Number(h.preco_iva || 0)
        })
        setHistoricoPrecos(map)
      }
    }
  }

  const fecharModal = () => {
    setModalEnc(null)
    setModalItens([])
  }

  const repetirEncomenda = async () => {
    if (!modalEnc || modalItens.length === 0) return
    setRepetindo(true)
    setMsg({ tipo: '', texto: '' })
    try {
      // Procurar produtos atuais por EAN e por Nome (usa preço atual, não histórico)
      const eans = modalItens.map(i => i.produto_ean).filter(Boolean)
      const nomes = modalItens.map(i => i.produto_nome).filter(Boolean)
      const queries = []
      // Só colunas necessárias — evita expor Transito*_Factura, notas_admin, desconto_supplier_pct
      // sinal_flexivel tem de vir: sem ele os itens do carrinho repetido nao
      // trazem a marca e o campo do sinal desaparecia numa encomenda que tinha
      // direito a ele — sem erro nenhum, so silencio.
      const colsRepetir = 'id, Nome, EAN, preco, preco_particular, Stock_Atual, foto_url, qtd_minima_compra, visivel_catalogo, sinal_flexivel, Email_Cliente'
      // Associar um produto a um cliente poe visivel_catalogo=false (fica privado dele).
      // Filtrar so por visivel_catalogo escondia ao cliente os SEUS proprios produtos
      // exclusivos — logo os que ele mais repete. Aceita os publicos OU os dele.
      const emailRepetir = viewAsEmail || modalEnc.cliente_email || user?.email || ''
      const visivelOuMeu = emailRepetir
        ? `visivel_catalogo.eq.true,Email_Cliente.eq.${emailRepetir}`
        : 'visivel_catalogo.eq.true'
      if (eans.length > 0)  queries.push(supabase.from('Produtos').select(colsRepetir).or(visivelOuMeu).in('EAN', eans))
      if (nomes.length > 0) queries.push(supabase.from('Produtos').select(colsRepetir).or(visivelOuMeu).in('Nome', nomes))
      const results = await Promise.all(queries)
      const todosProdutos = []
      const seenIds = new Set()
      for (const r of results) {
        if (r.error) throw new Error('BD: ' + r.error.message)
        for (const p of (r.data || [])) {
          if (!seenIds.has(p.id)) { seenIds.add(p.id); todosProdutos.push(p) }
        }
      }

      const indexEan  = new Map(todosProdutos.filter(p => p.EAN).map(p => [String(p.EAN), p]))
      const indexNome = new Map(todosProdutos.map(p => [p.Nome, p]))

      const naoEncontrados = []
      const precoMudou = []   // { nome, antes, agora }
      const qtdSubiu = []     // { nome, antes, agora }
      const novosItens = []
      for (const it of modalItens) {
        const prod = (it.produto_ean && indexEan.get(String(it.produto_ean))) || indexNome.get(it.produto_nome)
        if (!prod) { naoEncontrados.push(it.produto_nome); continue }
        // Verifica se cliente pode encomendar (produto privado de outro cliente já filtrado pela RLS)
        const precoBase = Number(prod.preco)
        const precoComIva = precoBase * (1 + IVA)
        // Preço de HOJE, nunca o da encomenda antiga — mas avisa-se, senão o
        // cliente repete a pensar que paga o mesmo e leva um susto no total.
        const precoAntigo = Number(it.preco_unitario || 0)
        if (precoAntigo > 0 && Math.abs(precoBase - precoAntigo) >= 0.01) {
          precoMudou.push({ nome: prod.Nome, antes: precoAntigo, agora: precoBase })
        }
        // O mínimo do produto pode ter subido desde a encomenda anterior.
        const qtdMin = Number(prod.qtd_minima_compra || 0)
        const qtdFinal = qtdMin > 0 ? Math.max(qtdMin, it.quantidade) : it.quantidade
        if (qtdFinal !== it.quantidade) {
          qtdSubiu.push({ nome: prod.Nome, antes: it.quantidade, agora: qtdFinal })
        }
        novosItens.push({
          ...prod,
          quantidade: qtdFinal,
          preco_unitario: precoBase,
          total_sem_iva: precoBase * qtdFinal,
          total_com_iva: precoComIva * qtdFinal,
          preco_com_iva: precoComIva,
          foto_url: prod.foto_url,
        })
      }

      if (novosItens.length === 0) {
        setMsg({ tipo: 'erro', texto: 'Nenhum dos produtos desta encomenda está disponível no catálogo atual.' })
        return
      }

      // Se já existe carrinho, pedir confirmação
      const carrinhoAtual = JSON.parse(localStorage.getItem('carrinho') || '[]')
      if (carrinhoAtual.length > 0) {
        const ok = window.confirm(`Já tem ${carrinhoAtual.length} item(ns) no carrinho. Substituir pelos itens desta encomenda?\n\nOK = substituir · Cancelar = manter o atual`)
        if (!ok) { setRepetindo(false); return }
      }

      guardarCarrinho(novosItens)

      const avisos = []
      if (naoEncontrados.length > 0) {
        avisos.push(`${naoEncontrados.length} produto(s) já não disponíveis foram ignorados: ${naoEncontrados.slice(0, 2).join(', ')}${naoEncontrados.length > 2 ? '…' : ''}`)
      }
      if (precoMudou.length > 0) {
        const d = precoMudou.slice(0, 2).map(p => `${p.nome} ${fmt(p.antes)} → ${fmt(p.agora)} €`).join('; ')
        avisos.push(`${precoMudou.length} artigo(s) mudaram de preço: ${d}${precoMudou.length > 2 ? '…' : ''}`)
      }
      if (qtdSubiu.length > 0) {
        const d = qtdSubiu.slice(0, 2).map(q => `${q.nome} ${q.antes} → ${q.agora} un.`).join('; ')
        avisos.push(`${qtdSubiu.length} artigo(s) ajustados à quantidade mínima actual: ${d}${qtdSubiu.length > 2 ? '…' : ''}`)
      }
      const aviso = avisos.length > 0 ? ` (${avisos.join(' · ')})` : ''
      // Se houve alteracoes, o cliente precisa de tempo para ler antes de saltar.
      const houveAvisos = avisos.length > 0
      setMsg({
        tipo: houveAvisos ? 'aviso' : 'ok',
        texto: `Carrinho preenchido com ${novosItens.length} item(ns).${aviso}${houveAvisos ? ' Confere os valores no carrinho antes de finalizar.' : ' A redirecionar para o catálogo…'}`,
      })
      fecharModal()
      setTimeout(() => {
        setActivePage?.('catalogo')
        if (!houveAvisos) setMsg({ tipo: '', texto: '' })
      }, houveAvisos ? 2600 : 1200)
    } catch (err) {
      setMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setRepetindo(false)
    }
  }

  // OS ESTADOS DE PAGAMENTO NAO ESTAVAM PREVISTOS AQUI.
  //
  // `pago_1_tranche` e `pago_2_tranche` caiam no `default` e o cliente lia
  // "Pendente". Uma encomenda com a 1.ª prestacao paga e aprovada dizia-lhe
  // pendente; e com o Naypes, que se paga a 100% a cabeca, uma encomenda
  // INTEGRALMENTE liquidada dizia o mesmo.
  //
  // A informacao certa ate estava no ecra — o painel dos pagamentos a vencer e
  // os comprovativos marcados como aprovados. Era so a palavra da etiqueta, que
  // fala do envio quando o cliente esta a pensar no pagamento. E "Pendente"
  // numa encomenda ja paga e o genero de coisa que gera um telefonema.
  const getEstadoInfo = (estado) => {
    switch (estado) {
      case 'pago': return { label: 'Pago', icon: CheckCircle, cor: C.success, bg: '#dff6dd' }
      case 'concluido': return { label: 'Concluída', icon: CheckCircle, cor: C.success, bg: '#dff6dd' }
      case 'cancelado': return { label: 'Cancelado', icon: AlertCircle, cor: C.danger, bg: '#fde7e9' }
      case 'enviado': return { label: 'Enviado', icon: Truck, cor: C.primary, bg: '#eff6fc' }
      // Pago por inteiro, a espera de seguir. Verde, porque do lado dele nao ha
      // mais nada a fazer.
      case 'pago_2_tranche': return { label: 'Pago — a preparar envio', icon: CheckCircle, cor: C.success, bg: '#dff6dd' }
      // Falta a 2.ª. Azul e nao amarelo: ele cumpriu a parte dele ate aqui, e o
      // que falta ja lhe e dito no painel dos pagamentos a vencer.
      case 'pago_1_tranche': return { label: '1.ª prestação paga', icon: CheckCircle, cor: C.primary, bg: '#eff6fc' }
      default: return { label: 'A aguardar pagamento', icon: Clock, cor: C.warning, bg: '#fff4ce' }
    }
  }

  // Cálculos para a modal — estrutura fiscal (CIVA art.16): desconto sobre s/IVA, IVA depois
  const mSemIvaBruto = modalEnc ? Number(modalEnc.total_sem_iva || 0) : 0  // subtotal s/IVA (sem desconto)
  const mDescontoPerc = modalEnc ? Number(modalEnc.desconto_percentagem || 0) : 0
  const mDescontoVal = modalEnc ? Number(modalEnc.desconto_valor || 0) : 0  // valor desconto s/IVA
  const mBaseTributavel = mSemIvaBruto - mDescontoVal
  const IVA_RATE = 0.23
  const mIva = mBaseTributavel * IVA_RATE
  const mTotal = modalEnc ? Number(modalEnc.total_final ?? (mBaseTributavel + mIva) ?? 0) : 0
  // Separados: com 50/50 davam o mesmo, com sinal de 8% são valores opostos.
  const m1a = modalEnc ? Math.round(primeiraPrestacao(modalEnc, mTotal) * 100) / 100 : 0
  const mFalta = Math.round((mTotal - m1a) * 100) / 100
  const mPct1a = pctDoTotal(m1a, mTotal)
  const mPortes = modalEnc ? Number(modalEnc.portes || 0) : 0
  const mPagaTudo = modalEnc ? pagaTudoDeUmaVez(modalEnc) : false
  // Legacy: total c/IVA bruto (antes do desconto) ainda armazenado em total_com_iva

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', padding: isMobile ? '12px 12px 0' : '0 28px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 4px' }}>Minhas Encomendas</h1>
          <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>{encomendas.length} encomenda(s)</p>
        </div>
        <button onClick={loadEncomendas} style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: C.textMuted }}>
          <RefreshCw size={13} /> Atualizar
        </button>
      </div>

      {/* 'aviso' = correu bem mas ha algo a conferir (precos mudaram, quantidades
          ajustadas). Sem este terceiro estado saia a vermelho, como se fosse erro. */}
      {msg.texto && (
        <div style={{ margin: isMobile ? '0 12px 14px' : '0 28px 14px', padding: '10px 14px', backgroundColor: msg.tipo === 'ok' ? '#dff6dd' : msg.tipo === 'aviso' ? '#fff4ce' : '#fde7e9', borderLeft: `4px solid ${msg.tipo === 'ok' ? C.success : msg.tipo === 'aviso' ? C.warning : C.danger}`, borderRadius: '2px', color: msg.tipo === 'ok' ? C.success : msg.tipo === 'aviso' ? '#92400e' : C.danger, fontSize: '13px', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: 1.5 }}>
          <span style={{ flexShrink: 0, marginTop: '2px', display: 'flex' }}>{msg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertCircle size={13} />}</span> {msg.texto}
        </div>
      )}

      {/* Pagamentos pendentes — só clientes (admin já vê tudo no Financeiro) */}
      {(() => {
        const pendentes = encomendas
          .filter(e => e.pago_1a_prestacao && !e.pago_2a_prestacao && !['cancelado','concluido'].includes(e.estado))
          .map(e => {
            const total = Number(e.total_final ?? e.total_com_iva ?? 0)
            // O que falta = total menos o que já entrou, nunca "a outra metade".
            const metade = Math.round((total - primeiraPrestacao(e, total)) * 100) / 100
            const portes = Number(e.portes || 0)
            // Se cliente pagou mas aguarda pagamento ao fornecedor: data nao definida ainda
            const aguardaFornecedor = !!e.aguarda_pagamento_fornecedor
            const dataPrev = aguardaFornecedor ? null : (e.data_envio ? new Date(e.data_envio) : (() => { const d = new Date(e.created_at); d.setDate(d.getDate() + Number(e.tempo_entrega_dias || 25)); return d })())
            const hoje = new Date(); hoje.setHours(0,0,0,0)
            const diasAteEnvio = dataPrev ? Math.ceil((dataPrev - hoje) / 86400000) : 9999
            return { ...e, _metade: metade, _portes: portes, _dataPrev: dataPrev, _diasAteEnvio: diasAteEnvio, _aguardaFornecedor: aguardaFornecedor }
          })
          .sort((a, b) => a._diasAteEnvio - b._diasAteEnvio)

        if (pendentes.length === 0) return null
        return (
          <div style={{ margin: isMobile ? '0 12px 14px' : '0 28px 16px', background: '#fff8e1', border: `1px solid ${C.warning}40`, borderRadius: '6px', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Clock size={14} color={C.warning} />
              <span style={{ fontSize: '12px', fontWeight: '700', color: C.warning, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Pagamentos a vencer · {pendentes.length}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {(pagamentosExpand ? pendentes : pendentes.slice(0, 5)).map(p => {
                const urgente = p._diasAteEnvio < 7
                return (
                  <div key={p.id}
                    onClick={() => abrirDetalhe(p)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: C.surface, borderRadius: '4px', cursor: 'pointer', border: `1px solid ${urgente ? C.danger : C.border}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: C.primary }}>
                        {p.numero_encomenda || '—'}
                        <span style={{ marginLeft: '8px', color: urgente ? C.danger : C.textMuted, fontWeight: '600' }}>
                          {p._aguardaFornecedor ? 'aguarda fornecedor' :
                           p._diasAteEnvio < 0 ? `há ${Math.abs(p._diasAteEnvio)} dia(s) — em atraso` :
                           p._diasAteEnvio === 0 ? 'hoje' :
                           `em ${p._diasAteEnvio} dia(s)`}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: C.textMuted }}>
                        {p._aguardaFornecedor
                          ? 'Chegada: será comunicada quando confirmarmos com o fornecedor'
                          : `Envio previsto: ${p._dataPrev.toLocaleDateString('pt-PT')}`}
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: C.warning }}>
                      {fmt(p._metade + p._portes)} €
                    </div>
                  </div>
                )
              })}
              {pendentes.length > 5 && (
                <button onClick={(e) => { e.stopPropagation(); setPagamentosExpand(v => !v) }}
                  style={{ background: 'none', border: 'none', color: C.primary, fontSize: '11px', fontWeight: '700', cursor: 'pointer', textAlign: 'center', padding: '4px 0' }}>
                  {pagamentosExpand ? '▲ Mostrar menos' : `▼ Mostrar mais ${pendentes.length - 5}`}
                </button>
              )}
            </div>
          </div>
        )
      })()}

      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '0 12px' : '0 28px' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar...</div>
        ) : encomendas.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: C.textMuted }}>
            <ShoppingCart size={40} style={{ opacity: 0.2, marginBottom: '10px' }} />
            <p>Nenhuma encomenda registada.</p>
          </div>
        ) : isMobile ? (
          /* MOBILE: lista de cards verticais */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '12px' }}>
            {encomendas.map(enc => {
              const estadoInfo = getEstadoInfo(enc.estado)
              const EstadoIcon = estadoInfo.icon
              return (
                <div
                  key={enc.id}
                  onClick={() => abrirDetalhe(enc)}
                  style={{ backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: C.primary }}>{enc.numero_encomenda || '—'}</span>
                    <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', backgroundColor: estadoInfo.bg, color: estadoInfo.cor, textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <EstadoIcon size={11} /> {estadoInfo.label}
                    </span>
                  </div>
                  {enc.aguarda_pagamento_fornecedor && (
                    <div title="A data de chegada será comunicada quando confirmarmos com o fornecedor" style={{ marginBottom: '6px', padding: '4px 10px', background: '#fff8e1', border: `1px solid ${C.warning}55`, borderRadius: '4px', fontSize: '10px', color: '#92400e', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                      <Clock size={10} /> Chegada será comunicada após confirmação com fornecedor
                    </div>
                  )}
                  <div style={{ fontSize: '13px', fontWeight: '600', color: C.text, marginBottom: '2px' }}>{enc.produto_nome}</div>
                  <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '8px' }}>
                    {enc.quantidade} unid. · {new Date(enc.created_at).toLocaleDateString('pt-PT')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: C.text }}>
                      {fmt(Number(enc.total_final ?? enc.total_com_iva ?? 0))} €
                      {Number(enc.desconto_valor || 0) > 0 && (
                        <span style={{ marginLeft: '6px', fontSize: '10px', color: '#107c10', fontWeight: '600' }}>
                          –{Number(enc.desconto_percentagem || 0)}%
                        </span>
                      )}
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: C.primary, fontSize: '12px', fontWeight: '700' }}>
                      <Eye size={13} /> Ver
                    </span>
                  </div>
                  {(enc.fatura_emitida_em || enc.tracking) && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${C.border}`, display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '11px' }}>
                      {enc.fatura_emitida_em && (
                        <span style={{ color: C.success, fontWeight: '700' }}>📄 {enc.fatura_numero || 'Faturada'}</span>
                      )}
                      {enc.tracking && (
                        <span style={{ color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          📦 {enc.transportadora || 'Tracking'}:{' '}
                          <TrackingLink transportadora={enc.transportadora} codigo={enc.tracking} />
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          /* DESKTOP: tabela em grid */
          <div style={{ backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr 1fr 1fr 1.5fr 80px', padding: '10px 16px', backgroundColor: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <span>Nº</span><span>Produto</span><span>Qtd</span><span>Total c/ IVA</span><span>Estado</span><span>Envio</span><span></span>
            </div>
            {encomendas.map((enc, i) => {
              const estadoInfo = getEstadoInfo(enc.estado)
              const EstadoIcon = estadoInfo.icon
              return (
                <div
                  key={enc.id}
                  onClick={() => abrirDetalhe(enc)}
                  style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr 1fr 1fr 1.5fr 80px', padding: '11px 16px', borderBottom: i < encomendas.length - 1 ? `1px solid ${C.border}` : 'none', alignItems: 'center', backgroundColor: C.surface, cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fafafa' }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = C.surface }}
                >
                  <div style={{ fontSize: '13px', fontWeight: '700', color: C.primary }}>{enc.numero_encomenda || '—'}</div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>{enc.produto_nome}</div>
                    {enc.produto_ean && (
                      <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                        EAN: {enc.produto_ean}
                        <CopyButton text={enc.produto_ean} size={11} title="Copiar EAN" />
                      </div>
                    )}
                    <div style={{ fontSize: '11px', color: C.textMuted }}>{new Date(enc.created_at).toLocaleDateString('pt-PT')}</div>
                  </div>
                  <div style={{ fontSize: '13px', color: C.text }}>{enc.quantidade} unid.</div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>
                    {fmt(Number(enc.total_final ?? enc.total_com_iva ?? 0))} €
                    {Number(enc.desconto_valor || 0) > 0 && (
                      <div style={{ fontSize: '10px', color: '#107c10', fontWeight: '600' }}>
                        (–{Number(enc.desconto_percentagem || 0)}% desc.)
                      </div>
                    )}
                  </div>
                  <div>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', backgroundColor: estadoInfo.bg, color: estadoInfo.cor, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px', width: 'fit-content' }}>
                      <EstadoIcon size={10} /> {estadoInfo.label}
                    </span>
                    {enc.aguarda_pagamento_fornecedor && (
                      <div title="Data de chegada será comunicada quando confirmarmos com o fornecedor" style={{ marginTop: '3px', padding: '2px 6px', background: '#fff8e1', border: `1px solid ${C.warning}55`, borderRadius: '4px', fontSize: '9px', color: '#92400e', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '3px', width: 'fit-content' }}>
                        <Clock size={8} /> Aguarda fornecedor
                      </div>
                    )}
                    {enc.fatura_emitida_em && (
                      <div style={{ marginTop: '3px', fontSize: '10px', color: C.success, fontWeight: '700', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        📄 {enc.fatura_numero || 'Faturada'}
                      </div>
                    )}
                  </div>
                  <div>
                    {enc.tracking ? (
                      <div>
                        <div style={{ fontSize: '11px', color: C.textMuted }}>
                          Tracking: <TrackingLink transportadora={enc.transportadora} codigo={enc.tracking} />
                        </div>
                        {enc.transportadora && <div style={{ fontSize: '11px', color: C.textMuted }}>{enc.transportadora}</div>}
                        {enc.data_envio && <div style={{ fontSize: '10px', color: C.textMuted }}>{new Date(enc.data_envio).toLocaleDateString('pt-PT')}</div>}
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', color: C.textMuted }}>—</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: C.primary, fontSize: '12px', fontWeight: '600' }}>
                      <Eye size={13} /> Ver
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── MODAL DETALHE ── */}
      {modalEnc && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) fecharModal() }}
        >
          <div style={{ background: C.surface, borderRadius: '6px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            {/* Header */}
            <div style={{ padding: '18px 22px', background: C.primary, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.8)' }}>Encomenda</p>
                <p style={{ margin: '3px 0 0', fontSize: '18px', fontWeight: '700' }}>{modalEnc.numero_encomenda || modalEnc.id.slice(0, 8).toUpperCase()}</p>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>{fmtDate(modalEnc.created_at)}</p>
              </div>
              <button onClick={fecharModal} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Estado + ação Repetir */}
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: C.textMuted, fontWeight: '600' }}>Estado:</span>
                  {(() => {
                    const e = getEstadoInfo(modalEnc.estado)
                    const I = e.icon
                    return (
                      <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', backgroundColor: e.bg, color: e.cor, textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <I size={11} /> {e.label}
                      </span>
                    )
                  })()}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {!viewAsEmail && modalEnc.estado === 'pendente' && (
                    <button
                      onClick={cancelarEncomenda}
                      disabled={cancelando}
                      title="Cancelar a encomenda (só possível enquanto pendente)"
                      style={{
                        padding: '8px 14px', height: '36px',
                        border: `1px solid ${C.danger}`, borderRadius: '4px',
                        background: '#fff', color: C.danger,
                        cursor: cancelando ? 'not-allowed' : 'pointer',
                        opacity: cancelando ? 0.6 : 1,
                        fontSize: '12px', fontWeight: '700',
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                      }}
                    >
                      {cancelando ? <><RefreshCw size={13} className="loading-spinner" /> A cancelar…</> : <><Ban size={13} /> Cancelar encomenda</>}
                    </button>
                  )}
                  <button
                    onClick={repetirEncomenda}
                    disabled={repetindo || modalLoading || modalItens.length === 0}
                    title="Adicionar os mesmos itens ao carrinho"
                    style={{
                      padding: '8px 14px', height: '36px',
                      border: 'none', borderRadius: '4px',
                      background: C.primary, color: '#fff',
                      cursor: (repetindo || modalLoading || modalItens.length === 0) ? 'not-allowed' : 'pointer',
                      opacity: (repetindo || modalLoading || modalItens.length === 0) ? 0.6 : 1,
                      fontSize: '12px', fontWeight: '700',
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                    }}
                  >
                    {repetindo ? <><RefreshCw size={13} className="loading-spinner" /> A preparar…</> : <><Repeat size={13} /> Repetir esta encomenda</>}
                  </button>
                </div>
              </div>

              {/* Itens */}
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Itens</p>
                {modalLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>A carregar itens...</div>
                ) : (
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: C.bg }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Produto</th>
                          <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Qtd</th>
                          <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Preço c/IVA</th>
                          <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total c/IVA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalItens.length === 0 ? (
                          <tr>
                            <td colSpan="4" style={{ padding: '12px', textAlign: 'center', color: C.textMuted, fontSize: '12px' }}>—</td>
                          </tr>
                        ) : modalItens.map((it, i) => {
                          const precoAnterior = historicoPrecos[it.produto_nome]
                          const precoAtual = Number(it.preco_iva || 0)
                          const delta = precoAnterior !== undefined ? precoAtual - precoAnterior : null
                          return (
                            <tr key={it.id || i} style={{ borderTop: `1px solid ${C.border}` }}>
                              <td style={{ padding: '8px 10px', color: C.text }}>
                                <div style={{ fontWeight: '600' }}>{it.produto_nome}</div>
                                {it.produto_ean && (
                                  <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    EAN: {it.produto_ean}
                                    <CopyButton text={it.produto_ean} size={11} title="Copiar EAN" />
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', color: C.text }}>{it.quantidade}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', color: C.text }}>
                                <div>{fmt(it.preco_iva)} €</div>
                                {delta !== null && Math.abs(delta) > 0.01 && (
                                  <div title={`Compra anterior: ${fmt(precoAnterior)} €`}
                                    style={{ fontSize: '10px', fontWeight: '700', color: delta > 0 ? C.danger : C.success, marginTop: '2px' }}>
                                    {delta > 0 ? '↑' : '↓'} {fmt(Math.abs(delta))} € vs anterior
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.text }}>{fmt(it.total_com_iva)} €</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Totais — estrutura fiscal (CIVA art.16) */}
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Totais</p>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '8px 12px', color: C.textMuted }}>Subtotal s/ IVA</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: C.text }}>{fmt(mSemIvaBruto)} €</td>
                      </tr>
                      {mDescontoVal > 0 && (
                        <tr style={{ background: '#fff4ce' }}>
                          <td style={{ padding: '8px 12px', color: C.warning, fontWeight: '700' }}>Desconto ({fmt(mDescontoPerc)}%)</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: C.warning, fontWeight: '700' }}>−{fmt(mDescontoVal)} €</td>
                        </tr>
                      )}
                      <tr style={{ background: C.bg, borderTop: `1px dashed ${C.border}` }}>
                        <td style={{ padding: '8px 12px', color: C.text, fontWeight: '700' }}>Base tributável</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', color: C.text }}>{fmt(mBaseTributavel)} €</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px 12px', color: C.textMuted }}>IVA (23%)</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: C.text }}>+{fmt(mIva)} €</td>
                      </tr>
                      <tr style={{ background: '#e8f5e9', borderTop: `2px solid ${C.success}` }}>
                        <td style={{ padding: '10px 12px', fontWeight: '700', color: C.success, fontSize: '14px' }}>Total a pagar</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700', color: C.success, fontSize: '17px' }}>{fmt(mTotal)} €</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Dados bancarios para transferencia - so mostra quando ha prestacao por pagar */}
              {(iban || nib) && (!modalEnc.comprovativo_1a_url || !modalEnc.comprovativo_2a_url) && (
                <div style={{ marginBottom: '16px', background: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '4px', padding: '12px 14px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '11px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    💳 Dados para Transferência Bancária
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {iban && (
                      <div>
                        <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>IBAN</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: C.text, fontFamily: 'monospace', letterSpacing: '0.5px', wordBreak: 'break-all', flex: 1, minWidth: 0 }}>{iban}</span>
                          <CopyButton text={iban} size={12} title="Copiar IBAN" />
                        </div>
                      </div>
                    )}
                    {nib && (
                      <div>
                        <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>NIB</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: C.text, fontFamily: 'monospace', wordBreak: 'break-all', flex: 1, minWidth: 0 }}>{nib}</span>
                          <CopyButton text={nib} size={12} title="Copiar NIB" />
                        </div>
                      </div>
                    )}
                    {bancoNome && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Banco</span>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: C.text, textAlign: 'right' }}>{bancoNome}</span>
                      </div>
                    )}
                    {bancoTitular && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Titular</span>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: C.text, textAlign: 'right' }}>{bancoTitular}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Comprovativos de pagamento */}
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Comprovativos de Pagamento</p>

                {/* Detectar se já está em modo "pagamento total" (1a e 2a apontam para o mesmo ficheiro) */}
                {(() => {
                  const modoTotal = modalEnc.comprovativo_1a_url
                    && modalEnc.comprovativo_2a_url
                    && modalEnc.comprovativo_1a_url === modalEnc.comprovativo_2a_url
                  if (modoTotal) {
                    const aprovadoTotal = modalEnc.pago_1a_prestacao && modalEnc.pago_2a_prestacao
                    return (
                      <div style={{ marginBottom: '10px', padding: '12px 14px', background: '#dff6dd', border: `1px solid ${C.success}`, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '20px' }}>{aprovadoTotal ? '💰' : '💯'}</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: C.success, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              Pagamento total (100%) {aprovadoTotal ? 'aprovado' : 'submetido'}
                              {aprovadoTotal && (
                                <span style={{ padding: '1px 7px', fontSize: '10px', fontWeight: '800', background: C.success, color: '#fff', borderRadius: '3px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>✓ Aprovado</span>
                              )}
                            </div>
                            <div style={{ fontSize: '11px', color: C.textMuted }}>
                              Enviado em {new Date(modalEnc.comprovativo_1a_em).toLocaleDateString('pt-PT')} {new Date(modalEnc.comprovativo_1a_em).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                              {aprovadoTotal && <span style={{ marginLeft: '6px', color: C.success, fontWeight: '600' }}>· não pode alterar</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button onClick={() => verComprovativo(modalEnc.comprovativo_1a_url)}
                            style={{ height: '32px', padding: '0 12px', border: `1px solid ${C.success}`, background: '#fff', color: C.success, borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                            Ver
                          </button>
                          {!aprovadoTotal && (
                            <button onClick={() => apagarComprovativo('total')}
                              disabled={!!uploadingComp}
                              style={{ height: '32px', padding: '0 12px', border: `1px solid ${C.danger}`, background: '#fff', color: C.danger, borderRadius: '3px', cursor: uploadingComp ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700' }}>
                              🗑 Apagar
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  }
                  // Botão "Pagar 100%" só se nenhuma prestação foi paga ainda
                  if (!modalEnc.comprovativo_1a_url && !modalEnc.comprovativo_2a_url) {
                    return (
                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '10px', padding: '12px 14px', border: `2px dashed ${C.success}`, borderRadius: '4px', background: '#e8f5e9', cursor: uploadingComp === 'total' ? 'not-allowed' : 'pointer', color: C.success, fontWeight: '700', fontSize: '13px', opacity: uploadingComp === 'total' ? 0.6 : 1 }}>
                        <span style={{ fontSize: '18px' }}>💳</span>
                        {uploadingComp === 'total'
                          ? 'A enviar comprovativo…'
                          : mPagaTudo
                            ? `Enviar comprovativo do pagamento — ${fmt(mTotal)} €`
                            : 'Pagar 100% agora (uma só transferência)'}
                        <input type="file" accept="application/pdf,image/*"
                          onChange={(ev) => { const f = ev.target.files?.[0]; if (f) uploadComprovativo(f, 'total'); ev.target.value = '' }}
                          style={{ display: 'none' }}
                          disabled={!!uploadingComp} />
                      </label>
                    )
                  }
                  return null
                })()}

                {/* Linhas individuais — só mostram se NÃO está em modo total.
                    E também não, numa encomenda de pagamento total, enquanto o
                    botão grande acima está no ecrã: eram duas caixas de upload
                    ao mesmo tempo para um único pagamento, e o cliente lia isso
                    como duas coisas para pagar. */}
                {!(mPagaTudo && !modalEnc.comprovativo_1a_url && !modalEnc.comprovativo_2a_url) &&
                 !(modalEnc.comprovativo_1a_url && modalEnc.comprovativo_2a_url && modalEnc.comprovativo_1a_url === modalEnc.comprovativo_2a_url) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* Uma linha por prestação — mas numa encomenda de pagamento
                      total só há uma, e o ficheiro dela vale pelas duas colunas
                      da BD (key 'total'). */}
                  {[
                    { key: mPagaTudo ? 'total' : '1a', label: mPagaTudo ? 'Pagamento 100%' : '1.ª Prestação', url: modalEnc.comprovativo_1a_url, em: modalEnc.comprovativo_1a_em, ref: modalEnc.comprovativo_1a_ref, pago: modalEnc.pago_1a_prestacao, rejEm: modalEnc.comprovativo_1a_rejeitado_em, rejMotivo: modalEnc.comprovativo_1a_rejeitado_motivo },
                    ...(mPagaTudo ? [] : [
                    { key: '2a', label: '2.ª Prestação', url: modalEnc.comprovativo_2a_url, em: modalEnc.comprovativo_2a_em, ref: modalEnc.comprovativo_2a_ref, pago: modalEnc.pago_2a_prestacao, rejEm: modalEnc.comprovativo_2a_rejeitado_em, rejMotivo: modalEnc.comprovativo_2a_rejeitado_motivo },
                    ]),
                  ].map(p => { const recusado = !!p.rejEm && !p.pago; return (
                    <div key={p.key} style={{ border: `1px solid ${p.pago ? C.success : recusado ? C.danger : p.url ? `${C.success}55` : C.border}`, background: p.pago ? '#dff6dd' : recusado ? '#fdecea' : p.url ? '#e8f5e9' : '#fafafa', borderRadius: '4px', padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '20px' }}>{p.pago ? '💰' : recusado ? '⚠️' : p.url ? '✅' : '📎'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: p.pago || p.url ? C.success : C.text, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {p.label}
                            {p.pago && (
                              <span style={{ padding: '1px 7px', fontSize: '10px', fontWeight: '800', background: C.success, color: '#fff', borderRadius: '3px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                ✓ Aprovado
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: C.textMuted }}>
                            {p.url ? `Enviado em ${new Date(p.em).toLocaleDateString('pt-PT')} ${new Date(p.em).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}` : 'Por anexar'}
                            {p.pago && <span style={{ marginLeft: '6px', color: C.success, fontWeight: '600' }}>· pagamento confirmado, não pode alterar</span>}
                          </div>
                        </div>
                        {p.url && (
                          <button onClick={() => verComprovativo(p.url)}
                            style={{ height: '32px', padding: '0 10px', border: `1px solid ${C.success}`, background: '#fff', color: C.success, borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                            Ver
                          </button>
                        )}
                        {!p.pago && p.url && (
                          <button onClick={() => apagarComprovativo(p.key)}
                            disabled={!!uploadingComp}
                            style={{ height: '32px', padding: '0 10px', border: `1px solid ${C.danger}`, background: '#fff', color: C.danger, borderRadius: '3px', cursor: uploadingComp ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {uploadingComp === p.key ? '…' : '🗑 Apagar'}
                          </button>
                        )}
                        {!p.pago && (
                          <label style={{ height: '32px', padding: '0 10px', border: `1px solid ${C.primary}`, background: p.url ? '#fff' : C.primary, color: p.url ? C.primary : '#fff', borderRadius: '3px', cursor: uploadingComp === p.key ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px', opacity: uploadingComp === p.key ? 0.7 : 1 }}>
                            {uploadingComp === p.key ? 'A enviar…' : (p.url ? 'Substituir' : 'Anexar')}
                            <input type="file" accept="application/pdf,image/*"
                              onChange={(ev) => { const f = ev.target.files?.[0]; if (f) uploadComprovativo(f, p.key); ev.target.value = '' }}
                              style={{ display: 'none' }}
                              disabled={!!uploadingComp} />
                          </label>
                        )}
                      </div>
                      {/* Recusa: o cliente tem de perceber o que correu mal e o que
                          fazer a seguir, senao fica a olhar para a encomenda parada. */}
                      {recusado && (
                        <div style={{ marginTop: '8px', padding: '9px 11px', background: '#fdecea', border: `1px solid ${C.danger}55`, borderRadius: '4px' }}>
                          <p style={{ margin: '0 0 3px', fontSize: '11.5px', fontWeight: '700', color: C.danger }}>
                            Comprovativo não aceite — {new Date(p.rejEm).toLocaleDateString('pt-PT')}
                          </p>
                          {p.rejMotivo && (
                            <p style={{ margin: '0 0 4px', fontSize: '11.5px', color: C.text, lineHeight: 1.5 }}>{p.rejMotivo}</p>
                          )}
                          <p style={{ margin: 0, fontSize: '11px', color: C.textMuted }}>
                            Corrige o que for necessário e anexa um novo comprovativo aqui.
                          </p>
                        </div>
                      )}
                      {p.ref && (
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: C.textMuted }}>
                          <span style={{ fontWeight: '600' }}>Ref. transferência:</span>
                          <span style={{ color: C.text, fontFamily: 'monospace', fontWeight: '700', letterSpacing: '0.5px' }}>{p.ref}</span>
                          <CopyButton text={p.ref} size={11} title="Copiar referência" />
                        </div>
                      )}
                    </div>
                  )})}
                </div>
                )}
                <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>
                  PDF ou imagem (max 10 MB). O administrador será notificado quando submeteres.
                </p>
              </div>

              {/* Plano de pagamento */}
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>
                  {mPagaTudo ? 'Pagamento — 100% na compra' : 'Plano de Pagamento — 2 prestações'}
                </p>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '8px 12px', color: C.textMuted }}>
                          {mPagaTudo ? 'Pagamento único (100% — na encomenda)' : `1.ª Prestação (${fmtPct(mPct1a)}% — pago na encomenda)`}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', color: C.success }}>{fmt(m1a)} €</td>
                      </tr>
                      {!mPagaTudo && (
                        <tr style={{ background: C.bg }}>
                          <td style={{ padding: '8px 12px', color: C.textMuted }}>2.ª Prestação ({fmtPct(Math.round((100 - mPct1a) * 10) / 10)}% — antes do envio)</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', color: C.warning }}>{fmt(mFalta + mPortes)} €</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Morada do cliente */}
              {modalEnc.entrega_nome && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Morada do Cliente</p>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', padding: '10px 12px', fontSize: '13px', color: C.text }}>
                    <div style={{ fontWeight: '600' }}>{modalEnc.entrega_nome}</div>
                    {modalEnc.entrega_nif && (
                      <div style={{ color: C.textMuted, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        NIF: {modalEnc.entrega_nif}
                        <CopyButton text={modalEnc.entrega_nif} size={11} title="Copiar NIF" />
                      </div>
                    )}
                    <div style={{ color: C.textMuted, fontSize: '12px' }}>{modalEnc.entrega_morada}</div>
                    <div style={{ color: C.textMuted, fontSize: '12px' }}>{modalEnc.entrega_cod_postal} {modalEnc.entrega_pais}</div>
                    {modalEnc.entrega_telefone && (
                      <div style={{ color: C.textMuted, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        {modalEnc.entrega_telefone}
                        <CopyButton text={modalEnc.entrega_telefone} size={11} title="Copiar telefone" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Fatura */}
              {modalEnc.fatura_emitida_em && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Fatura</p>
                  <div style={{ border: `1px solid ${C.success}40`, background: '#e8f5e9', borderRadius: '4px', padding: '12px 14px', fontSize: '13px', color: C.text, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>📄</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '700', color: C.success }}>{modalEnc.fatura_numero || 'Faturada'}</div>
                      <div style={{ fontSize: '11px', color: C.textMuted }}>Emitida em {fmtDate(modalEnc.fatura_emitida_em)}{modalEnc.fatura_pdf_url ? ' · enviada por email' : ''}</div>
                    </div>
                    {modalEnc.fatura_pdf_url && (
                      <button onClick={async () => {
                        try {
                          const { data, error } = await supabase.storage.from('faturas').createSignedUrl(modalEnc.fatura_pdf_url, 3600)
                          if (error || !data?.signedUrl) throw new Error(error?.message || 'erro a gerar link')
                          window.open(data.signedUrl, '_blank')
                        } catch (e) {
                          setMsg({ tipo: 'erro', texto: 'Erro a abrir fatura: ' + (e?.message || String(e)) })
                        }
                      }}
                        style={{ padding: '8px 14px', height: '36px', border: 'none', borderRadius: '4px', background: C.success, color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
                        Descarregar PDF
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Tracking */}
              {modalEnc.tracking && (() => {
                const det = detectTracking(modalEnc.transportadora, modalEnc.tracking)
                return (
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Envio</p>
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', padding: '10px 12px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Tracking:{' '}
                        <TrackingLink transportadora={modalEnc.transportadora} codigo={modalEnc.tracking} size="lg" />
                      </div>
                      {modalEnc.transportadora && <div style={{ color: C.textMuted, fontSize: '12px', marginTop: '2px' }}>{modalEnc.transportadora}</div>}
                      {modalEnc.data_envio && <div style={{ color: C.textMuted, fontSize: '12px' }}>Enviado: {fmtDate(modalEnc.data_envio)}</div>}
                      {det && (
                        <a
                          href={det.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: C.primary, color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', fontWeight: '700' }}
                        >
                          <ExternalLink size={12} /> Seguir encomenda{det.fallback ? '' : ` (${det.nome})`}
                        </a>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* Notas */}
              {modalEnc.notas && (
                <div>
                  <p style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>Notas</p>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', padding: '10px 12px', fontSize: '12px', color: C.textMuted, whiteSpace: 'pre-wrap' }}>
                    {modalEnc.notas}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
